import { useCallback } from 'react'
import type { AgentMode } from '@/index'
import type { ClaudeSession, UserInputDraft } from '@/features/events/conversation/state/types'
import { findEventByRefId } from '@/features/events/conversation/state/types'
import type { UseCommandSubmitterReturn } from '@/features/events/conversation/controls'
import type { UserOption } from '@/features/events/shared/types'

interface UseSubmitUserInputDraftHandlerParams {
  readonly session: ClaudeSession | null
  readonly selectedDraft: UserInputDraft | null
  readonly submitter: UseCommandSubmitterReturn
}

interface UseSubmitUserInputDraftHandlerReturn {
  readonly handleSubmit: (prompt: string, agentMode?: AgentMode | null) => Promise<boolean>
}

function useSubmitUserInputDraftHandler({
  session,
  selectedDraft,
  submitter,
}: UseSubmitUserInputDraftHandlerParams): UseSubmitUserInputDraftHandlerReturn {
  const handleSubmit = useCallback(
    async (prompt: string, agentMode?: AgentMode | null): Promise<boolean> => {
      if (!session || !selectedDraft) return false

      try {
        switch (selectedDraft.type) {
          case 'user_reply':
            await submitter.submitReply(prompt, agentMode ?? undefined)
            break
          case 'injected_prompt':
            await submitter.submitInject(prompt, agentMode ?? undefined)
            break
          case 'custom_option': {
            if (!selectedDraft.refEventId) return false
            const event = findEventByRefId(session, selectedDraft.refEventId)
            if (!event) return false

            const agentDialogue = event.notificationContext?.agentDialogue
            const userOptions = agentDialogue?.userOptions
            if (!userOptions || userOptions.length === 0) return false

            const maxIndex = Math.max(...userOptions.map((opt: UserOption) => opt.index))
            const customOption = userOptions.find((opt: UserOption) => opt.index === maxIndex)
            if (!customOption) return false

            await submitter.submitUserOption(event, customOption, prompt, agentMode ?? undefined)
            break
          }
          case 'custom_answer':
          case 'resume_session':
            // resume_session is not submittable from Drafts — always initiated from EventsPage via useResumeSession
            return false
          default:
            return assertNever(selectedDraft.type)
        }
        return true
      } catch {
        return false
      }
    },
    [session, selectedDraft, submitter],
  )

  return { handleSubmit }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled draft type: ${String(value)}`)
}

export type { UseSubmitUserInputDraftHandlerParams, UseSubmitUserInputDraftHandlerReturn }
export { useSubmitUserInputDraftHandler }
